import { newDocumentPath } from '../lib/document-entry';
import { useWorkTab } from '@/app/work-tabs/work-tab-context';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, FileText, Plus, Search } from 'lucide-react';
import {
  documentStatusSchema,
  documentTypeSchema,
  listViewDefaultColumns,
  LIST_VIEW_COLUMNS,
  documentSortFieldSchema,
  type DocumentType,
} from '@erp/shared';
import { ListViewToolbar, OrderedListCells, useListView } from '@/features/list-views';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataCard, DataCardList } from '@/components/common/data-card';
import { formatDate, formatMoney } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/features/auth';
import { CategoryLegacyDocuments } from '@/features/legacy-documents';
import { useDocumentList, type DocumentListParams } from '@/features/documents/api/use-documents';
import {
  DocumentStatusBadge,
  PaymentStatusBadge,
} from '@/features/documents/components/document-badges';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
const TABS: readonly (DocumentType | 'ALL')[] = [
  'ALL',
  'QUOTE',
  'ORDER',
  'DELIVERY_NOTE',
  'INVOICE',
  'CANCELLATION',
  'CREDIT_NOTE',
];

type PaymentFilter = 'ALL' | 'OPEN' | 'OVERDUE' | 'PAID';

interface DocumentsRouteProps {
  initialType?: DocumentType | 'ALL';
  initialPayment?: PaymentFilter;
  titleKey?: string;
}

export function DocumentsRoute({
  initialType = 'ALL',
  initialPayment = 'ALL',
  titleKey = 'documents.title',
}: DocumentsRouteProps = {}): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const workTab = useWorkTab();
  const [searchParams] = useSearchParams();
  const { can } = usePermissions();
  const searchRef = useRef<HTMLInputElement>(null);

  const requestedPayment = searchParams.get('payment') ?? initialPayment;
  const views = useListView('documents', {
    filters: {
      ...(initialType === 'ALL' ? {} : { type: initialType }),
      ...Object.fromEntries(
        [...searchParams].filter(([key]) =>
          [
            'type',
            'status',
            'dateFrom',
            'dateTo',
            'paymentStatus',
            'openOnly',
            'overdueOnly',
          ].includes(key),
        ),
      ),
      ...(requestedPayment === 'OPEN'
        ? { openOnly: 'true' }
        : requestedPayment === 'OVERDUE'
          ? { overdueOnly: 'true' }
          : requestedPayment === 'PAID'
            ? { paymentStatus: 'PAID' }
            : {}),
    },
    sort: searchParams.get('sort') ?? 'documentDate:desc',
    columns: listViewDefaultColumns('documents'),
    fieldRefs: {},
    ...(searchParams.get('q') ? { search: searchParams.get('q') ?? '' } : {}),
  });
  const tab = documentTypeSchema.safeParse(views.state.filters['type']).data ?? 'ALL';
  const setTab = (value: DocumentType | 'ALL'): void =>
    views.setFilter('type', value === 'ALL' ? undefined : value);
  const search = views.state.search ?? '';
  const setSearch = (value: string): void => views.commit({ ...views.state, search: value }, true);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const status = views.state.filters['status'] ?? 'ALL';
  const setStatus = (value: string): void =>
    views.setFilter('status', value === 'ALL' ? undefined : value);
  const payment: PaymentFilter =
    views.state.filters['openOnly'] === 'true'
      ? 'OPEN'
      : views.state.filters['overdueOnly'] === 'true'
        ? 'OVERDUE'
        : views.state.filters['paymentStatus'] === 'PAID'
          ? 'PAID'
          : 'ALL';
  const setPayment = (value: PaymentFilter): void => {
    const filters = { ...views.state.filters };
    delete filters['openOnly'];
    delete filters['overdueOnly'];
    delete filters['paymentStatus'];
    if (value === 'OPEN') filters['openOnly'] = 'true';
    if (value === 'OVERDUE') filters['overdueOnly'] = 'true';
    if (value === 'PAID') filters['paymentStatus'] = 'PAID';
    views.commit({ ...views.state, filters });
  };
  const dateFrom = views.effectiveFilters['dateFrom'] ?? '';
  const dateTo = views.effectiveFilters['dateTo'] ?? '';
  const setDateFrom = (value: string): void => views.setFilter('dateFrom', value);
  const setDateTo = (value: string): void => views.setFilter('dateTo', value);
  const { page, setPage } = views;
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (!workTab.active) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('[role="dialog"]')) return;
      const isInput = target !== null && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);

      if (event.key === '/' && !isInput) {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (
        can('document:write') &&
        event.key.toLowerCase() === 'n' &&
        !isInput &&
        !event.metaKey &&
        !event.ctrlKey
      ) {
        event.preventDefault();
        navigate(newDocumentPath(tab));
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, can, tab, workTab.active]);

  const params = useMemo<DocumentListParams>(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      sort: views.state.sort,
      q: debouncedSearch.trim() === '' ? undefined : debouncedSearch.trim(),
      type: (views.state.filters['type'] ?? '').split(',').filter(Boolean),
      status: (views.state.filters['status'] ?? '').split(',').filter(Boolean),
      paymentStatus: (views.state.filters['paymentStatus'] ?? '').split(',').filter(Boolean),
      openOnly: views.state.filters['openOnly'] === 'true' ? true : undefined,
      overdueOnly: views.state.filters['overdueOnly'] === 'true' ? true : undefined,
      dateFrom: dateFrom === '' ? undefined : dateFrom,
      dateTo: dateTo === '' ? undefined : dateTo,
    }),
    [page, debouncedSearch, dateFrom, dateTo, views.state.sort, views.state.filters],
  );

  const query = useDocumentList(params, views.ready && debouncedSearch === search);
  const facets = query.data?.facets;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t(titleKey)}</h1>
          <p className="text-sm text-muted-foreground">{t('documents.subtitle')}</p>
        </div>
        {can('document:write') ? (
          <Button type="button" onClick={() => navigate(newDocumentPath(tab))}>
            <Plus aria-hidden="true" className="mr-2 h-4 w-4" />
            {tab === 'ALL'
              ? t('documents.actions.create')
              : t('documentEntry.createType', { type: t(`documents.types.${tab}`) })}
          </Button>
        ) : null}
      </header>

      <ListViewToolbar
        controller={views}
        columnLabel={(id) =>
          t(`documents.columns.${id}`, {
            defaultValue: t(`documents.filters.${id}`, { defaultValue: id }),
          })
        }
      />
      <Tabs
        value={tab}
        onValueChange={(value) => {
          setTab(value as DocumentType | 'ALL');
        }}
      >
        <TabsList>
          {TABS.map((entry) => (
            <TabsTrigger key={entry} value={entry}>
              {entry === 'ALL' ? t('documents.tabs.all') : t(`documents.types.${entry}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[16rem] flex-1">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            ref={searchRef}
            className="pl-8"
            aria-label={t('documents.filters.search')}
            placeholder={t('documents.filters.searchPlaceholder')}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
        </div>

        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value);
          }}
        >
          <SelectTrigger className="w-44" aria-label={t('documents.filters.status')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t('documents.filters.allStatuses')}</SelectItem>
            {documentStatusSchema.options.map((option) => (
              <SelectItem key={option} value={option}>
                {t(`documents.statuses.${option}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(['ALL', 'OPEN', 'OVERDUE', 'PAID'] as const).map((entry) => (
          <Button
            key={entry}
            type="button"
            size="sm"
            variant={payment === entry ? 'default' : 'outline'}
            onClick={() => {
              setPayment(entry);
            }}
          >
            {entry === 'OVERDUE' ? (
              <AlertTriangle aria-hidden="true" className="mr-2 h-4 w-4" />
            ) : null}
            {t(`documents.filters.payment.${entry}`)}
            {entry === 'OVERDUE' && facets !== undefined ? (
              <span className="ml-2 tabular-nums text-xs">{facets.overdue}</span>
            ) : null}
          </Button>
        ))}

        <select
          className="h-11 rounded-md border bg-background px-2 md:h-9"
          aria-label={t('listViews.period')}
          value={views.state.period ?? ''}
          onChange={(e) => {
            const next = { ...views.state, filters: { ...views.state.filters } };
            delete next.filters['dateFrom'];
            delete next.filters['dateTo'];
            if (e.target.value === 'THIS_WEEK' || e.target.value === 'THIS_MONTH')
              next.period = e.target.value;
            else delete next.period;
            views.commit(next);
          }}
        >
          <option value="">{t('listViews.fixedDates')}</option>
          <option value="THIS_WEEK">{t('listViews.thisWeek')}</option>
          <option value="THIS_MONTH">{t('listViews.thisMonth')}</option>
        </select>
        <select
          className="h-11 rounded-md border bg-background px-2 md:h-9"
          aria-label={t('listViews.sort')}
          value={views.state.sort}
          onChange={(e) => views.commit({ ...views.state, sort: e.target.value })}
        >
          {documentSortFieldSchema.options.flatMap((field) =>
            ['asc', 'desc'].map((direction) => (
              <option key={`${field}:${direction}`} value={`${field}:${direction}`}>
                {t(`documents.columns.${field}`, {
                  defaultValue: t(`listViews.sortLabels.${field}`, { defaultValue: field }),
                })}{' '}
                · {t(`listViews.${direction}`)}
              </option>
            )),
          )}
        </select>
        <Input
          type="date"
          disabled={views.state.period !== undefined}
          className="w-40"
          aria-label={t('documents.filters.dateFrom')}
          value={dateFrom}
          onChange={(event) => {
            setDateFrom(event.target.value);
          }}
        />
        <Input
          type="date"
          disabled={views.state.period !== undefined}
          className="w-40"
          aria-label={t('documents.filters.dateTo')}
          value={dateTo}
          onChange={(event) => {
            setDateTo(event.target.value);
          }}
        />
      </div>

      <h2 className="text-lg font-semibold">
        {t('legacy.nativeTitle')}
        {query.data ? ` (${query.data.meta.total})` : ''}
      </h2>
      {views.error || views.issues.length > 0 ? null : !views.ready ||
        query.isPending ||
        query.isPlaceholderData ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : query.isError ? (
        <div className="space-y-3 rounded-md border border-border p-6">
          <p role="alert" className="text-sm text-destructive">
            {query.error.message}
          </p>
          <Button type="button" variant="outline" onClick={() => void query.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : query.data.data.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border p-6 text-center">
          <FileText aria-hidden="true" className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('documents.states.empty')}</p>
          {can('document:write') ? (
            <Button type="button" onClick={() => navigate(newDocumentPath(tab))}>
              {tab === 'ALL'
                ? t('documents.actions.create')
                : t('documentEntry.createType', { type: t(`documents.types.${tab}`) })}
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <DataCardList>
            {query.data.data.map((entry) => (
              <DataCard
                key={entry.id}
                title={entry.number}
                subtitle={
                  views.state.columns.some((c) => c.id === 'customer' && c.visible)
                    ? entry.customerName
                    : undefined
                }
                badge={
                  views.state.columns.some((c) => c.id === 'status' && c.visible) ? (
                    <DocumentStatusBadge status={entry.status} />
                  ) : undefined
                }
                metric={
                  views.state.columns.some((c) => c.id === 'grossTotal' && c.visible)
                    ? formatMoney(entry.grossTotal, entry.currency, i18n.language)
                    : undefined
                }
                expandLabel={t('common.showDetails')}
                collapseLabel={t('common.hideDetails')}
                onOpen={() => navigate(`/documents/${entry.id}`)}
                details={[
                  {
                    id: 'type',
                    label: t('documents.columns.type'),
                    value: t(`documents.types.${entry.type}`),
                  },
                  {
                    id: 'documentDate',
                    label: t('documents.columns.documentDate'),
                    value: formatDate(entry.documentDate, i18n.language),
                  },
                  {
                    id: 'dueDate',
                    label: t('documents.columns.dueDate'),
                    value: (
                      <span
                        className={cn(
                          entry.overdueDays !== null && entry.overdueDays > 0
                            ? 'text-destructive'
                            : undefined,
                        )}
                      >
                        {formatDate(entry.dueDate, i18n.language)}
                      </span>
                    ),
                  },
                  {
                    id: 'status',
                    label: t('documents.columns.status'),
                    value: <PaymentStatusBadge status={entry.paymentStatus} />,
                  },
                ]
                  .filter((field) =>
                    views.state.columns.some((c) => c.id === field.id && c.visible),
                  )
                  .sort(
                    (a, b) =>
                      views.state.columns.findIndex((c) => c.id === a.id) -
                      views.state.columns.findIndex((c) => c.id === b.id),
                  )}
              />
            ))}
          </DataCardList>

          <div className="hidden overflow-x-auto rounded-md border border-border md:block">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <OrderedListCells columns={views.state.columns} ids={LIST_VIEW_COLUMNS.documents}>
                    <th scope="col" className="px-3 py-2">
                      {t('documents.columns.number')}
                    </th>
                    <th scope="col" className="px-3 py-2">
                      {t('documents.columns.type')}
                    </th>
                    <th scope="col" className="px-3 py-2">
                      {t('documents.columns.customer')}
                    </th>
                    <th scope="col" className="px-3 py-2">
                      {t('documents.columns.documentDate')}
                    </th>
                    <th scope="col" className="px-3 py-2">
                      {t('documents.columns.dueDate')}
                    </th>
                    <th scope="col" className="px-3 py-2">
                      {t('documents.columns.status')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      {t('documents.columns.grossTotal')}
                    </th>
                  </OrderedListCells>
                </tr>
              </thead>
              <tbody>
                {query.data.data.map((entry) => (
                  <tr
                    key={entry.id}
                    tabIndex={0}
                    className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/40 focus:bg-muted/60 focus:outline-none"
                    onClick={() => navigate(`/documents/${entry.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        navigate(`/documents/${entry.id}`);
                      }
                    }}
                  >
                    <OrderedListCells
                      columns={views.state.columns}
                      ids={LIST_VIEW_COLUMNS.documents}
                    >
                      <td className="px-3 py-2 font-medium tabular-nums">{entry.number}</td>
                      <td className="px-3 py-2">{t(`documents.types.${entry.type}`)}</td>
                      <td className="px-3 py-2">
                        <span className="block">{entry.customerName}</span>
                        <span className="block text-xs text-muted-foreground">
                          {entry.customerNumber}
                        </span>
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        {formatDate(entry.documentDate, i18n.language)}
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2 tabular-nums',
                          entry.overdueDays !== null && entry.overdueDays > 0
                            ? 'text-destructive'
                            : undefined,
                        )}
                      >
                        {formatDate(entry.dueDate, i18n.language)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-1">
                          <DocumentStatusBadge status={entry.status} />
                          <PaymentStatusBadge status={entry.paymentStatus} />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(entry.grossTotal, entry.currency, i18n.language)}
                      </td>
                    </OrderedListCells>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {t('documents.states.pagination', {
                page: query.data.meta.page,
                totalPages: Math.max(query.data.meta.totalPages, 1),
                total: query.data.meta.total,
              })}
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={query.data.meta.page <= 1}
                onClick={() => setPage((value) => Math.max(value - 1, 1))}
              >
                {t('common.previous')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={query.data.meta.page >= query.data.meta.totalPages}
                onClick={() => setPage((value) => value + 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </>
      )}
      {initialPayment === 'ALL' && (
        <CategoryLegacyDocuments
          category={
            params.type?.length
              ? params.type.flatMap((type) => {
                  const parsed = documentTypeSchema.safeParse(type);
                  return parsed.success ? [parsed.data] : [];
                })
              : 'ALL'
          }
          search={debouncedSearch}
          dateFrom={dateFrom}
          dateTo={dateTo}
          sort={views.state.sort}
          ready={views.ready && debouncedSearch === search}
          operativeFiltersActive={Boolean(
            params.status?.length ||
            params.paymentStatus?.length ||
            params.openOnly ||
            params.overdueOnly,
          )}
        />
      )}
    </div>
  );
}
